'use client';

import * as React from 'react';
import { useRef, useState, useEffect, useMemo } from 'react';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { ThemedImage } from '@/components/ui/themed-image';
import { useCategories } from '@/hooks/use-categories';
import { useProducts, useProductsByCategory } from '@/hooks/use-products';
import type { Category } from '@/types';

// Маппинг ID категорий на локальные изображения
const CATEGORY_IMAGES: Record<number, string> = {
  1: '/category/rolldog.png',      // Ролл-доги
  2: '/category/sets.png',         // Сеты
  3: '/category/holodny.png',      // Холодные
  4: '/category/zapetchiny.png',   // Запеченные🔥
  5: '/category/temperny.png',     // Темпурные🔥
  7: '/category/sous.png',         // Соусы
  11: '/category/opaleni.png',     // Опаленные
  14: '/category/ot shefa.png',    // От шефа
};

// Получение изображения категории (локальное или из API)
const getCategoryImage = (category: { id: number; image_url?: string }): string | undefined => {
  return CATEGORY_IMAGES[category.id] || category.image_url;
};

interface CategoryCarouselProps {
  className?: string;
  onCategoryClick?: (category: Category) => void;
}

export function CategoryCarousel({ className, onCategoryClick }: CategoryCarouselProps) {
  const { data: allCategories = [], isLoading: isLoadingCategories } = useCategories();
  const { data: products, isLoading: isLoadingProducts } = useProducts();
  const productsByCategory = useProductsByCategory(products);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showArrow, setShowArrow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const isLoading = isLoadingCategories || isLoadingProducts;

  // Получаем категории из продуктов и дополняем данными из API категорий
  const visibleCategories = useMemo(() => {
    // Сначала берем категории из продуктов (те, что реально используются)
    const categoriesFromProducts = Array.from(productsByCategory.entries()).map(
      ([, { category }]) => ({
        id: category.id,
        name: category.name,
      })
    );

    // Находим полные данные категорий из API (только те, что вернул публичный API — видимые)
    const fullCategories = categoriesFromProducts
      .map((catFromProducts) => {
        return allCategories.find((c) => c.id === catFromProducts.id);
      })
      .filter((cat): cat is Category => cat != null)
      .sort((a, b) => a.sort_order - b.sort_order)
      .slice(0, 8);

    return fullCategories;
  }, [productsByCategory, allCategories]);

  // Заглушки для отображения, если категории загружаются
  const displayCategories = isLoading
    ? Array.from({ length: 8 }, (_, i) => ({
        id: i,
        name: '',
        image_url: undefined,
        is_visible: true,
        sort_order: i,
      }))
    : visibleCategories;

  // Проверяем, нужна ли стрелка прокрутки
  useEffect(() => {
    const checkScroll = () => {
      if (!scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const hasOverflow = container.scrollWidth > container.clientWidth;
      setShowArrow(hasOverflow);

      const canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth - 10;
      const canScrollLeft = container.scrollLeft > 10;
      setCanScrollRight(canScrollRight);
      setCanScrollLeft(canScrollLeft);
    };

    checkScroll();
    
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    
      return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [displayCategories]);

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });
  };

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleCategoryClick = (category: Category | typeof displayCategories[0]) => {
    // Если это заглушка или категорий нет, не делаем ничего
    if (isLoading || !category.name || visibleCategories.length === 0) {
      return;
    }
    
    // Скроллим к секции категории в каталоге
    const categoryElement = document.getElementById(`category-${category.id}`);
    if (categoryElement) {
      categoryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    onCategoryClick?.(category as Category);
  };

  return (
    <section className={cn('relative w-full py-1 md:py-2', className)}>
      <div className="max-w-7xl mx-auto overflow-visible">
        <div className="relative min-h-[100px] sm:min-h-[120px] md:min-h-[140px]">
          {/* Обертка для предотвращения обрезания при увеличении */}
          <div className="overflow-visible py-2">
            {/* Контейнер с категориями */}
            <div
              ref={scrollContainerRef}
              className="flex gap-2.5 sm:gap-3 overflow-x-auto hide-scrollbar scroll-smooth"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {displayCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex flex-col items-center flex-shrink-0"
                >
                  <button
                    onClick={() => handleCategoryClick(category)}
                    className="group relative w-28 h-20 sm:w-32 sm:h-[88px] md:w-36 md:h-24 rounded-2xl overflow-hidden transition-transform duration-200 hover:scale-105 active:scale-95"
                  >
                    {/* Фон изображения категории */}
                    <div className="absolute inset-0">
                      {(() => {
                        const imageUrl = getCategoryImage(category);
                        return imageUrl && !isLoading ? (
                          <ThemedImage
                            src={imageUrl}
                            alt={category.name}
                            fill
                            className="object-cover scale-110"
                          />
                        ) : (
                          <div className={cn(
                            "w-full h-full bg-[var(--primary)]/20 dark:bg-[var(--primary)]/30",
                            isLoading && "animate-pulse"
                          )} />
                        );
                      })()}
                    </div>
                  </button>

                  {/* Название категории под карточкой */}
                  {category.name && !isLoading && (
                    <span className="mt-1.5 text-[11px] sm:text-xs font-medium text-[var(--text-primary)] text-center max-w-28 sm:max-w-32 md:max-w-36 line-clamp-2">
                      {category.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Стрелка прокрутки влево */}
          {showArrow && canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm text-[var(--primary)] hover:bg-white shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
              aria-label="Прокрутить влево"
            >
              <ArrowLeft weight="bold" className="h-5 w-5" />
            </button>
          )}

          {/* Стрелка прокрутки справа */}
          {showArrow && canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm text-[var(--primary)] hover:bg-white shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
              aria-label="Прокрутить вправо"
            >
              <ArrowRight weight="bold" className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

